import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import OfferCalendarSidebarDatePicker from '../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import OfferCalendarSidebarDatePickerV1 from '../Filters/CalendarSidebarPicker/V1/OfferCalendarSidebarDatePicker'
import OfferDurationOptions from '../Filters/Duration/OfferDurationOptions'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import { min } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { interactDuration } from 'analytics/optimizely/events'
import { track } from 'analytics/analytics'
import { OptimizelyFeatureFlags, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

interface Props {
  className?: string;
  onViewPackageOptions?: () => void;
  activeFilter?: OfferPageFilterTypes;
  capacities?: Array<App.PackageRoomCapacity>;
  offer: App.Offer;
  durationOptions: Array<OfferDurationOption>;
}

function FlashHotelSidebarFilters(props: Props) {
  const {
    onViewPackageOptions,
    activeFilter,
    capacities,
    offer,
    durationOptions,
  } = props

  const isMobile = useIsMobileScreen()
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)

  const trackInteractionDurationEvent = useCallback(() => {
    track('interact-duration', interactDuration())
  }, [])

  const closeFilter = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    }
  }, [dispatch, onUserAnalyticsAction])

  const closeFilterWithEvent = useCallback(() => {
    closeFilter()
    trackInteractionDurationEvent()
  }, [closeFilter, trackInteractionDurationEvent])

  const backToFilterList = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'all' })
    }
  }, [dispatch])

  const backToFilterListWithEvent = useCallback(() => {
    backToFilterList()
    trackInteractionDurationEvent()
  }, [backToFilterList, trackInteractionDurationEvent])

  const onApply = useCallback(() => {
    closeFilter()
    onViewPackageOptions?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [closeFilter, onViewPackageOptions, onUserAnalyticsAction])

  const onApplyDuration = useCallback(() => {
    trackInteractionDurationEvent()
  }, [trackInteractionDurationEvent])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])
  const useNewCalendar = useOptimizelyExperiment(OptimizelyFeatureFlags.useNewMobileCalendar)

  return (
    <ModalBase
      isOpen={delayedOpen && isMobile}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'airport' && <>
          <ModalHeader
            title="Where will you depart from?"
            subtitle="Select your preferred airport"
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title="Who is going?"
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <ModalContent>
              <OfferCapacityWithAgesSelection
                capacities={capacities}
                disableMultiRoom={isFlightForceBundled(offer)}
                submit
              />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && <>
          {!useNewCalendar && <OfferCalendarSidebarDatePickerV1
            onClose={closeFilter}
            onBack={backToFilterList}
          />}
          {useNewCalendar && <OfferCalendarSidebarDatePicker
            onClose={closeFilter}
            onBack={backToFilterList}
          />}
        </>}
        {activeFilter === 'duration' && <>
          <ModalHeader
            title="How long do you want to stay?"
            subtitle={minimumDuration > 1 ? `Minimum stay for this offer is ${pluralizeToString('night', minimumDuration)}` : undefined}
            onCloseButtonClick={closeFilterWithEvent}
            onBackButtonClick={backToFilterListWithEvent}
          />
          <ModalBody>
            <ModalContent>
              <OfferDurationOptions onSelect={trackInteractionDurationEvent}/>
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
              onClick: onApplyDuration,
            }}
          />
        </>}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  activeFilter: state.activeFilter,
  offer: state.offer,
  durationOptions: getDurationOptions(state, appState),
}))(FlashHotelSidebarFilters)
